<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("employee-page.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("employee-page.error") }}</div>
    <template v-else>
      <!-- top bar: title + breadcrumb + quick search + actions -->
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ employee.fullName }}</div>
          <div class="crumb">
            {{ $t("common.company") }} / {{ $t("employee-page.breadcrumbEmployees") }} / {{ employee.fullName }}
          </div>
        </div>
        <div class="row" style="gap: 8px">
          <div class="searchbar grow" style="min-width: 220px">
            🔎
            <input v-model="claimSearch" :placeholder="$t('employee-page.searchPlaceholder')" />
          </div>
          <button class="btn ghost sm" type="button" :disabled="true" :title="$t('common.comingSoon')">
            {{ $t("employee-page.edit") }}
          </button>
          <button
            v-if="employee.status !== 'left'"
            class="btn sm"
            :class="employee.status === 'suspended' ? 'ok' : 'danger'"
            type="button"
            :disabled="acting"
            @click="toggleSuspend"
          >
            {{ employee.status === "suspended" ? $t("employee-page.reactivate") : $t("employee-page.suspend") }}
          </button>
          <button
            class="btn ghost sm"
            type="button"
            :disabled="acting || employee.status === 'left'"
            @click="registerLeave"
          >
            {{ $t("employee-page.leave") }}
          </button>
        </div>
      </div>

      <!-- identity header -->
      <div class="panel">
        <div class="panel__body">
          <div class="row between">
            <div class="u" style="gap: 12px">
              <span class="avatar" style="width: 48px; height: 48px; font-size: 16px">{{ monogram }}</span>
              <div>
                <b style="font-size: 16px">{{ employee.fullName }}</b>
                <div class="sm muted">
                  {{ $t("employee-page.cardLabel") }} <span class="mono">{{ employee.cardNumber }}</span>
                </div>
              </div>
            </div>
            <span class="tag" :class="statusTagClass">
              <span v-if="employee.status === 'active'" class="dotc"></span>
              {{ statusLabel(employee.status) }}
            </span>
          </div>
        </div>
      </div>

      <div class="grid2 mt">
        <!-- personal details -->
        <div class="panel">
          <div class="panel__head"><h4>{{ $t("employee-page.personal.title") }}</h4></div>
          <div class="panel__body">
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.personal.nationalId") }}</span>
              <b class="mono">{{ nationalId }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.personal.birthDate") }}</span>
              <b>{{ fDate(employee.birthDate) }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.personal.gender") }}</span>
              <b>{{ genderLabel(employee.gender) }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.personal.phone") }}</span>
              <b class="mono">{{ employee.phone }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.personal.email") }}</span>
              <b>{{ employee.email }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.personal.hireDate") }}</span>
              <b>{{ fDate(employee.hireDate) }}</b>
            </div>
          </div>
        </div>

        <!-- policy & coverage -->
        <div class="panel">
          <div class="panel__head"><h4>{{ $t("employee-page.policy.title") }}</h4></div>
          <div class="panel__body">
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.policy.number") }}</span>
              <b class="mono">{{ policy ? policy.number : employee.policyNumber || "—" }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.policy.validity") }}</span>
              <b>{{ policy ? fDate(policy.validFrom) + " — " + fDate(policy.validTo) : "—" }}</b>
            </div>
            <div class="divider"></div>
            <div class="row between">
              <span class="muted sm">{{ $t("employee-page.policy.annualCap") }}</span>
              <b><span class="mono">{{ policy && policy.annualCap != null ? fInt(policy.annualCap) : usage && usage.cap != null ? fInt(usage.cap) : "—" }}</span> {{ $t("employee-page.currency") }}</b>
            </div>
            <div class="divider"></div>
            <div>
              <span class="muted sm">{{ $t("employee-page.policy.coverages") }}</span>
              <div class="row" style="margin-top: 6px; gap: 6px">
                <span v-for="cov in coverageTags" :key="cov" class="tag ok">{{ cov }}</span>
                <span v-if="coverageTags.length === 0" class="muted sm">—</span>
              </div>
            </div>
            <div v-if="usage" class="callout mt">
              <template v-if="usage.remaining != null">
                <b>{{ $t("employee-page.policy.remaining") }}: <span class="mono">{{ fInt(usage.remaining) }}</span>
                  {{ $t("employee-page.currency") }}</b>
              </template>
              <template v-if="usage.consumed != null">
                <template v-if="usage.remaining != null"> — </template>
                {{ $t("employee-page.policy.consumed") }}:
                <span class="mono">{{ fInt(usage.consumed) }}</span> {{ $t("employee-page.currency") }}
              </template>
            </div>
            <div v-if="usage && usagePct != null" class="mt" data-test="usage-bar">
              <div class="ubar" role="progressbar" :aria-valuenow="usagePct" aria-valuemin="0" aria-valuemax="100">
                <div class="ubar__fill" :class="{ 'is-high': usagePct >= 90 }" :style="{ width: usagePct + '%' }"></div>
              </div>
              <div class="xs muted" style="margin-top: 4px">{{ $t("employee-page.policy.usedPct", { n: usagePct }) }}</div>
            </div>
            <div v-if="!usage && policy" class="note info mt">{{ $t("employee-page.policy.usageUnavailable") }}</div>
          </div>
        </div>
      </div>

      <!-- claims table -->
      <div class="panel mt">
        <div class="panel__head">
          <h4>{{ $t("employee-page.claims.title") }}</h4>
          <span class="tag muted" style="margin-inline-start: auto">
            {{ $t("employee-page.claims.count", { n: claims.length }) }}
          </span>
        </div>
        <div class="panel__body p0">
          <div class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th>{{ $t("employee-page.claims.colNo") }}</th>
                  <th>{{ $t("employee-page.claims.colType") }}</th>
                  <th>{{ $t("employee-page.claims.colAmount") }}</th>
                  <th>{{ $t("employee-page.claims.colDate") }}</th>
                  <th>{{ $t("employee-page.claims.colStatus") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in visibleClaims" :key="c.id">
                  <td class="mono">#{{ c.id }}</td>
                  <td>{{ c.serviceType }}</td>
                  <td><span class="mono">{{ fInt(c.amount) }}</span> {{ $t("employee-page.currency") }}</td>
                  <td>{{ fDate(c.invoiceDate || c.createdAt) }}</td>
                  <td><span class="tag" :class="tagClass(c.status)">{{ claimStatusLabel(c.status) }}</span></td>
                </tr>
                <tr v-if="!visibleClaims.length">
                  <td colspan="5" class="muted" style="text-align: center; padding: 22px">
                    {{ $t("employee-page.claims.empty") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- follow-up log (derived from the employee's own dates) -->
      <div class="panel mt">
        <div class="panel__head"><h4>{{ $t("employee-page.timeline.title") }}</h4></div>
        <div class="panel__body">
          <div class="tl-log">
            <div class="tl-item done">
              <span class="tl-dot"></span>
              <div>
                <b>{{ $t("employee-page.timeline.hired") }}</b>
                <div class="sm muted">{{ fDate(employee.hireDate) }}</div>
              </div>
            </div>
            <div class="tl-item now">
              <span class="tl-dot"></span>
              <div>
                <b>{{ $t("employee-page.timeline.current") }}: {{ statusLabel(employee.status) }}</b>
                <div class="sm muted">{{ $t("employee-page.timeline.now") }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPatch, apiPost } from "../../api/vivo/http";
import { companyPath } from "../../api/vivo/session";
import { initials, groupDigits, claimTagClass, sortByDateDesc, filterClaims, coverageUsage, usagePercent } from "./employee-page-vm";

const EMP_STATUS_TAG = { active: "ok", suspended: "review", left: "danger" };

export default {
  name: "VivoEmployeePage",
  data() {
    return {
      employee: null,
      policy: null,
      coverages: [],
      claims: [],
      claimSearch: "",
      loading: true,
      error: false,
      acting: false,
    };
  },
  async mounted() {
    const id = this.$route.params.id || "1";
    try {
      this.employee = await apiGet(`/employees/${id}`);
    } catch (e) {
      this.error = true;
      this.loading = false;
      return;
    }
    // best-effort enrichments — the profile still renders if any of these fail.
    // §4 ClaimsQueryDto has no employeeId filter (sending it → 400), so fetch the
    // company's claims and narrow to this employee client-side. (Server-side
    // employeeId filtering on /claims is a backend contract gap — logged.)
    try {
      const res = await apiGet("/claims", { limit: 100 });
      const all = (res && res.data) || [];
      this.claims = all.filter((c) => String(c.employeeId) === String(id));
    } catch (e) {
      this.claims = [];
    }
    if (this.employee.policyId) {
      // §4 HR read: the company's policies, then pick the employee's one.
      try {
        const pols = await apiGet(companyPath("policies"), { limit: 100 });
        const list = (pols && pols.data) || [];
        this.policy = list.find((p) => p.id === this.employee.policyId) || null;
      } catch (e) {
        this.policy = null;
      }
    }
    // coverage tags for the policy panel (§4 HR read of /coverages).
    try {
      const cov = await apiGet("/coverages", { limit: 100 });
      this.coverages = (cov && cov.data) || [];
    } catch (e) {
      this.coverages = [];
    }
    this.loading = false;
  },
  computed: {
    monogram() {
      return this.employee ? initials(this.employee.fullName) : "—";
    },
    nationalId() {
      return this.employee ? groupDigits(this.employee.nationalId, 4) : "—";
    },
    statusTagClass() {
      return EMP_STATUS_TAG[this.employee.status] || "muted";
    },
    usage() {
      return coverageUsage(this.employee, this.policy);
    },
    usagePct() {
      return usagePercent(this.usage);
    },
    visibleClaims() {
      return filterClaims(sortByDateDesc(this.claims), this.claimSearch);
    },
    coverageTags() {
      if (!this.policy || !this.policy.coverageIds) return [];
      const isAr = String(this.$i18n.locale).indexOf("ar") === 0;
      const map = {};
      for (let i = 0; i < this.coverages.length; i++) {
        map[this.coverages[i].id] = this.coverages[i];
      }
      const out = [];
      for (let j = 0; j < this.policy.coverageIds.length; j++) {
        const c = map[this.policy.coverageIds[j]];
        if (c) out.push(isAr ? c.nameAr : c.nameEn || c.nameAr);
      }
      return out;
    },
  },
  methods: {
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    fDate(iso) {
      if (!iso) return "—";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
    genderLabel(g) {
      // Render the real value from §4 (gender is now returned by the employee DTO);
      // show a neutral dash when absent instead of fabricating "male".
      return g === "male" || g === "female"
        ? this.$t("employee-page.gender." + g)
        : "—";
    },
    statusLabel(status) {
      return this.$t("employee-page.status." + status);
    },
    tagClass(status) {
      return claimTagClass(status);
    },
    claimStatusLabel(status) {
      return this.$t("employee-page.claimStatus." + status);
    },
    async toggleSuspend() {
      const id = this.$route.params.id || "1";
      const next = this.employee.status === "suspended" ? "active" : "suspended";
      this.acting = true;
      try {
        this.employee = await apiPatch(`/employees/${id}/status`, { status: next });
      } catch (e) {
        /* keep current state on failure */
      } finally {
        this.acting = false;
      }
    },
    async registerLeave() {
      const id = this.$route.params.id || "1";
      this.acting = true;
      try {
        this.employee = await apiPost(`/employees/${id}/leave`);
      } catch (e) {
        /* keep current state on failure */
      } finally {
        this.acting = false;
      }
    },
  },
};
</script>

<style scoped>
.ubar {
  height: 8px;
  border-radius: 999px;
  background: var(--v-line-2);
  overflow: hidden;
}
.ubar__fill {
  height: 100%;
  background: var(--v-brand);
  border-radius: 999px;
}
.ubar__fill.is-high {
  background: var(--v-danger);
}
.tl-log {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.tl-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.tl-item .tl-dot {
  flex: none;
  width: 12px;
  height: 12px;
  margin-top: 3px;
  background: var(--v-ok);
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--v-ok-soft);
}
.tl-item.now .tl-dot {
  background: var(--v-brand);
  box-shadow: 0 0 0 2px var(--v-brand-soft);
}
</style>
